/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';
import Backbone from 'backbone';

import lazyLoadView from '@/util/lazyLoadView';
import AssessmentCommunicationFileUploadTmpl from '@/fileupload/AssessmentCommunicationFileUploadTmpl.hbs';
import showModal from '@/util/showModal';
import SearchUsersModalView from '@/views/SearchUsersModalView/SearchUsersModalView';
import showErrorMessage from '@/util/showErrorMessage';
import OrderStatuses from '@/enums/OrderStatuses';

import AssessmentHeaderView from '../../views/AssessmentHeaderView';
import SideNavSectionView from '../../../common/views/SideNavSectionView';
import LoadAssessmentDetails from '../../loaders/AssessmentDetailsLoader';
import GetAssessmentCommunications from '../../services/GetAssessmentCommunications';
import SubmitAssessmentCommunication from '../../services/SubmitAssessmentCommunications';
import LoadCommunications from '../../../common/loaders/CommunicationsLoader';
import ReassignAssessment from '../../services/ReassignAssessment';

import tmplAssessmentPage from './AssessmentPage.hbs';
import './AssessmentPage.css';

const AssessmentPage = View.extend({
  template: tmplAssessmentPage,

  className: 'assessment-page container-fluid',

  ui: {
    assessmentHeader: '.assessment-header',
    sections: '.sections-container'
  },

  regions: {
    assessmentHeader: '@ui.assessmentHeader',
    sections: '@ui.sections'
  },

  childViewEvents: {
    reassign: 'onReassignCRS'
  },

  onRender() {
    const isCRSOwner = this.model.get('isCRSOwner');
    const showReassignBtn =
      this.model.get('assessmentStatusId') === OrderStatuses.IN_REVIEW_REVIEW && isCRSOwner;

    this.showChildView(
      'assessmentHeader',
      new AssessmentHeaderView({
        statusId: this.model.get('assessmentStatusId'),
        showReassignBtn,
        model: new Backbone.Model({
          commandName: this.model.get('commandName'),
          assignedCRS: this.model.get('assignedCRS'),
          assessmentId: this.model.get('assessmentId'),
          assessmentStatus: this.model.get('assessmentStatus'),
          assessmentType: this.model.get('assessmentType'),
          isCRSOwner
        })
      })
    );

    const assessmentId = this.model.get('assessmentId');
    const assessmentStatusId = this.model.get('assessmentStatusId');
    const isReadOnly = this.model.get('isReadOnly');
    const isInternalUser = this.model.get('isInternalUser');
    const assignedCRS = this.model.get('assignedCRS');
    const customerName = this.model.get('customerName');
    const returnable = this.model.get('returnable');
    const editable = !EXTERNAL && isCRSOwner && (assessmentStatusId === '2' || assessmentStatusId === '3');
    const isFileEditable = !isReadOnly || editable;
    const isSubmittable = (EXTERNAL && assessmentStatusId === '1') || editable;
    const returnLink = this.model.get('returnLink');
    const returnLabel = this.model.get('returnLabel');

    const assessmentData = {
      assessmentId,
      assessmentStatusId,
      isReadOnly,
      isInternalUser,
      assignedCRS,
      customerName,
      returnable,
      returnLink,
      returnLabel,
      isFileEditable,
      isSubmittable
    };

    if (EXTERNAL) {
      this.showChildView('sections', new lazyLoadView(() => LoadAssessmentDetails(assessmentData)));
    } else {
      this.showChildView(
        'sections',
        new SideNavSectionView({
          useNavigationButtons: true,
          activeSectionId: this.options.activeSectionId,
          model: new Backbone.Model({
            returnLabel: 'Return to Dashboard',
            returnLink: isInternalUser ? '#dashboard' : '#ext/dashboard',
            commandName: this.model.get('commandName'),
            assignedCRS: this.model.get('assignedCRS'),
            assessmentId: this.model.get('assessmentId'),
            assessmentStatus: this.model.get('assessmentStatus'),
            assessmentType: this.model.get('assessmentType'),
            isReadOnly: this.model.get('isReadOnly'),
            editable
          }),
          collection: new Backbone.Collection([
            {
              id: 'assessmentDetails',
              title: 'Assessment Details',
              loader: LoadAssessmentDetails,
              loaderOptions: assessmentData,
              view: lazyLoadView(() => LoadAssessmentDetails(assessmentData))
            },
            {
              id: 'assessmentCommunication',
              title: 'Communication',
              view: lazyLoadView(() =>
                LoadCommunications({
                  editable,
                  metadata: {
                    objectId: assessmentId
                  },
                  assessmentData,
                  getCommunicationsService: GetAssessmentCommunications,
                  submitCommunicationService: SubmitAssessmentCommunication,
                  submitCommunicationAttachmentService: AssessmentCommunicationFileUploadTmpl,
                  pathToFileId:
                    'html.head.script.Envelope.Body.UploadAssessmentCommunicationAttachmentResponse.AttachmentID.__text'
                })
              )
            }
          ])
        })
      );
    }
  },

  async onReassignCRS() {
    const user = await showModal({
      title: 'Reassign CRS',
      view: new SearchUsersModalView(),
      dialogClasses: 'modal-lg'
    });

    try {
      await ReassignAssessment({
        assessmentId: this.model.get('assessmentId'),
        crsId: user.id
      });
      this.getChildView('assessmentHeader').setAssignedCRS(user.userName);
    } catch (err) {
      this.showReassignmentErrorMessage();
    }
  },

  showReassignmentErrorMessage() {
    showErrorMessage({
      errorCode: 'reassignmentError',
      errorTitle: 'Reassignment Error',
      errorMessage:
        'An error occurred during reassignment. Try again or contact an administrator if your issue persists.'
    });
  }
});

export default AssessmentPage;
